// The legacy portal's session parameter — the value its own pages carry as
// `?RANDOMNUM=` when they link to each other. We need it to deep-link INTO those
// pages (config/legacyPortal.js).
//
// WHY THIS FILE EXISTS AT ALL: the legacy app keeps the value in localStorage under
// `sessionParameter`, but that storage belongs to the LEGACY origin
// (https://uat-www.webaccountlink.com). We are a cross-origin iframe, so
// `localStorage.getItem('sessionParameter')` in OUR document returns null no matter
// what DevTools shows on the parent's origin. The value has to be handed to us.
//
// Shape observed on the parent (a JSON-encoded array):
//   ["260902044506", "Abel, Access", "DEMO-CLIENT", "711ba7b6-…", "Relius"]
//     [0] RANDOMNUM — a YYMMDDHHMMSS stamp (cache-buster), the value the .aspx links use
//     [1] participant display name   [2] client code
//     [3] session GUID               [4] recordkeeping system
// Only [0] is read here. [3] is NOT put in a URL: a session id in a query string leaks
// through history, referrers and access logs.
const RANDOMNUM_INDEX = 0;

// 1 · Handed over by the parent in the postMessage handshake. In memory only, for the
//     same reason as the embed token: a reload must re-ask the parent rather than
//     reuse a stale session (see embeddedToken.js).
let fromHandshake = null;

// 3 · Bootstrap query param, read ONCE at module load — React Router rewrites the URL
//     on the first navigation, so reading it lazily would miss it.
const fromQuery = (() => {
  try {
    const q = new URLSearchParams(window.location.search);
    return q.get('sessionParameter') || q.get('rnd') || null;
  } catch {
    return null;
  }
})();

/**
 * Record the parent's `sessionParameter` from the handshake reply. Accepts the raw
 * array, a JSON string of it, or a bare RANDOMNUM string — the .NET side may send
 * whichever is cheapest for it.
 */
export function setLegacySessionParameter(value) {
  fromHandshake = value == null ? null : value;
}

/** Drop it alongside the session token (logout / failed handshake). */
export function clearLegacySessionParameter() {
  fromHandshake = null;
}

/** Pull RANDOMNUM out of whatever shape we were given. */
function toRandomNum(value) {
  if (value == null) return null;
  if (Array.isArray(value)) return value[RANDOMNUM_INDEX] ?? null;
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (trimmed.startsWith('[')) {
    try {
      const parsed = JSON.parse(trimmed);
      return Array.isArray(parsed) ? (parsed[RANDOMNUM_INDEX] ?? null) : null;
    } catch {
      return null;
    }
  }
  return trimmed; // already a bare RANDOMNUM
}

/**
 * RANDOMNUM for a legacy deep link, or null when nobody has given us one.
 * Precedence: parent handshake → same-origin localStorage → bootstrap query param.
 *
 * The localStorage read is not dead code: it is the branch that works if V3 is ever
 * served FROM the legacy origin, where the key really is ours to read.
 */
export function getLegacyRandomNum() {
  const fromParent = toRandomNum(fromHandshake);
  if (fromParent) return fromParent;

  let stored = null;
  try {
    stored = toRandomNum(window.localStorage.getItem('sessionParameter'));
  } catch {
    stored = null; // storage blocked (third-party-cookie / privacy settings)
  }
  if (stored) return stored;

  return toRandomNum(fromQuery);
}

export default getLegacyRandomNum;
